import { useState, useMemo, useCallback, useEffect, useRef, Fragment } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { type MockInspection, type MockInspectionResult } from '@/mocks/qms';
import { qcService } from '@/services/qcService';
import { useToast } from '@/contexts/ToastContext';
import NewInspectionSlideOver from './components/NewInspectionSlideOver';
import InspectionFormModal from './components/InspectionFormModal';
import ViewInspectionModal from './components/ViewInspectionModal';
import NCRPromptModal from './components/NCRPromptModal';
import SupplierQualityTab from './components/SupplierQualityTab';
import SearchableSelect from '@/components/base/SearchableSelect';

const statusBadge = (status: MockInspection['status']) => {
  const map: Record<string, string> = {
    PASSED: 'bg-emerald-100 text-emerald-700',
    FAILED: 'bg-red-100 text-red-700',
    PENDING: 'bg-amber-100 text-amber-700',
    IN_PROGRESS: 'bg-sky-100 text-sky-700',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
};

export const formatDateDisplay = (dateStr?: string | null) => {
  if (!dateStr) return '—';
  const clean = String(dateStr).split('T')[0];
  const parts = clean.split('-');
  if (parts.length === 3) {
    const [y, m, d] = parts;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = parseInt(m, 10) - 1;
    const monthName = months[monthIndex] || m;
    return `${d} ${monthName} ${y}`;
  }
  return clean;
};

interface GRNGroup {
  grnKey: string;
  sourceNumber: string;
  sourceType: string;
  supplierName: string;
  scheduledDate: string;
  items: MockInspection[];
  totalQty: number;
  sampleQty: number;
  passedCount: number;
  failedCount: number;
  pendingCount: number;
  inProgressCount: number;
  overallStatus: 'PASSED' | 'FAILED' | 'PENDING' | 'IN_PROGRESS';
}

import { useWarehouseStore } from '@/stores/warehouseStore';

export default function IncomingQCPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { selectedWarehouseId } = useWarehouseStore();

  const [inspections, setInspections] = useState<MockInspection[]>([]);
  const [results, setResults] = useState<MockInspectionResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsedGrns, setCollapsedGrns] = useState<Record<string, boolean>>({});

  const toggleGrn = (key: string) => {
    setCollapsedGrns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const collapseAll = () => {
    const next: Record<string, boolean> = {};
    groupedInspections.forEach((g) => {
      if (g.items.length > 1) next[g.grnKey] = true;
    });
    setCollapsedGrns(next);
  };

  const expandAll = () => {
    setCollapsedGrns({});
  };

  const loadInspections = useCallback(async () => {
    try {
      setLoading(true);
      const data = await qcService.getInspections({
        type: 'INCOMING',
        warehouseId: selectedWarehouseId || undefined,
      });
      setInspections(data || []);
    } catch (err) {
      console.error('Error loading incoming inspections:', err);
      setInspections([]);
    } finally {
      setLoading(false);
    }
  }, [selectedWarehouseId]);

  useEffect(() => {
    loadInspections();
  }, [loadInspections]);

  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  const [showNewInspection, setShowNewInspection] = useState(false);
  const [activeInspection, setActiveInspection] = useState<MockInspection | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showNCRPrompt, setShowNCRPrompt] = useState(false);
  const [ncrInspection, setNcrInspection] = useState<MockInspection | null>(null);

  const [activeTab, setActiveTab] = useState<'inspections' | 'supplier_quality'>('inspections');
  const [prefillGrnId, setPrefillGrnId] = useState<string | undefined>(undefined);
  const [prefillData, setPrefillData] = useState<{
    grnId?: string;
    grnNumber?: string;
    supplierId?: string;
    supplierName?: string;
    warehouseId?: string;
    items?: Array<{
      itemId: string;
      itemName: string;
      qty: number;
      unit: string;
      batchNumber?: string | null;
      lotNumber?: string | null;
      qualityStatus?: string;
    }>;
  } | null>(null);
  const autoCreateProcessed = useRef(false);

  useEffect(() => {
    if (autoCreateProcessed.current) return;
    const state = location.state as {
      autoCreate?: boolean;
      grnId?: string;
      grnNumber?: string;
      supplierId?: string;
      supplierName?: string;
      warehouseId?: string;
      items?: Array<{
        itemId: string;
        itemName: string;
        qty: number;
        unit: string;
        batchNumber?: string | null;
        lotNumber?: string | null;
        qualityStatus?: string;
      }>;
    } | null;
    if (state?.autoCreate && state?.grnId) {
      autoCreateProcessed.current = true;
      setPrefillGrnId(state.grnId);
      setPrefillData({
        grnId: state.grnId,
        grnNumber: state.grnNumber,
        supplierId: state.supplierId,
        supplierName: state.supplierName,
        warehouseId: state.warehouseId,
        items: state.items,
      });
      setShowNewInspection(true);
    }
  }, [location]);

  const incomingInspections = useMemo(
    () => inspections.filter((i) => i.type === 'INCOMING'),
    [inspections]
  );

  const filtered = useMemo(() => {
    return incomingInspections.filter((i) => {
      if (statusFilter && i.status !== statusFilter) return false;
      if (dateFrom && i.scheduledDate < dateFrom) return false;
      if (dateTo && i.scheduledDate > dateTo) return false;
      if (sourceFilter && !i.sourceNumber.toLowerCase().includes(sourceFilter.toLowerCase())) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          i.inspectionNumber.toLowerCase().includes(q) ||
          i.itemName.toLowerCase().includes(q) ||
          i.sourceNumber.toLowerCase().includes(q) ||
          i.checklistName.toLowerCase().includes(q) ||
          (i.inspectorName && i.inspectorName.toLowerCase().includes(q))
        );
      }
      return true;
    });
  }, [incomingInspections, statusFilter, search, dateFrom, dateTo, sourceFilter]);

  const groupedInspections = useMemo(() => {
    const groups: GRNGroup[] = [];
    const grnMap = new Map<string, GRNGroup>();

    filtered.forEach((insp) => {
      const key =
        insp.sourceNumber && insp.sourceNumber.trim() !== '—' && insp.sourceNumber.trim() !== ''
          ? insp.sourceNumber.trim()
          : `single-${insp.id}`;

      if (!grnMap.has(key)) {
        const newGroup: GRNGroup = {
          grnKey: key,
          sourceNumber: insp.sourceNumber || '—',
          sourceType: insp.sourceType || 'GRN',
          supplierName: insp.supplierName || '—',
          scheduledDate: insp.scheduledDate,
          items: [],
          totalQty: 0,
          sampleQty: 0,
          passedCount: 0,
          failedCount: 0,
          pendingCount: 0,
          inProgressCount: 0,
          overallStatus: 'PENDING',
        };
        grnMap.set(key, newGroup);
        groups.push(newGroup);
      }

      const grp = grnMap.get(key)!;
      grp.items.push(insp);
      grp.totalQty += insp.totalQty || 0;
      grp.sampleQty += insp.sampleQty || 0;

      if (insp.status === 'PASSED') grp.passedCount++;
      else if (insp.status === 'FAILED') grp.failedCount++;
      else if (insp.status === 'IN_PROGRESS') grp.inProgressCount++;
      else grp.pendingCount++;
    });

    groups.forEach((g) => {
      if (g.failedCount > 0) g.overallStatus = 'FAILED';
      else if (g.inProgressCount > 0) g.overallStatus = 'IN_PROGRESS';
      else if (g.passedCount === g.items.length && g.items.length > 0) g.overallStatus = 'PASSED';
      else g.overallStatus = 'PENDING';
    });

    return groups;
  }, [filtered]);

  const stats = useMemo(() => {
    const total = incomingInspections.length;
    const passed = incomingInspections.filter((i) => i.status === 'PASSED').length;
    const failed = incomingInspections.filter((i) => i.status === 'FAILED').length;
    const pending = incomingInspections.filter((i) => i.status === 'PENDING').length;
    return { total, passed, failed, pending };
  }, [incomingInspections]);

  const handleNewInspection = useCallback(async (newInsp: MockInspection[]) => {
    try {
      for (const insp of newInsp) {
        await qcService.createInspection(insp);
      }
      await loadInspections();
    } catch (err) {
      console.error('Error creating incoming inspection:', err);
    } finally {
      setShowNewInspection(false);
      setPrefillGrnId(undefined);
      setPrefillData(null);
    }
  }, [loadInspections]);

  const [activeResults, setActiveResults] = useState<MockInspectionResult[]>([]);

  const handleStartInspection = useCallback(async (insp: MockInspection) => {
    setActiveInspection(insp);
    try {
      const data = await qcService.getInspectionById(insp.id);
      setActiveResults(data?.results || []);
    } catch {
      setActiveResults([]);
    }
  }, []);

  const handleViewInspection = useCallback(async (insp: MockInspection) => {
    setActiveInspection(insp);
    setShowViewModal(true);
    try {
      const data = await qcService.getInspectionById(insp.id);
      setActiveResults(data?.results || []);
    } catch {
      setActiveResults([]);
    }
  }, []);

  const handleSubmitInspection = useCallback(
    async (
      newResults: MockInspectionResult[],
      overallStatus: 'PASSED' | 'FAILED'
    ) => {
      if (!activeInspection) return;

      const targetInsp = activeInspection;
      try {
        await qcService.submitInspectionResults(targetInsp.id, {
          results: newResults,
          overallStatus,
        });

        if (overallStatus === 'PASSED') {
          const qty = targetInsp.totalQty || targetInsp.sampleQty || 1;
          toast.success(
            `Inspection ${targetInsp.inspectionNumber} PASSED! Stock increased by ${qty} ${targetInsp.unit || 'units'}.`
          );
        } else {
          toast.warning(
            `Inspection ${targetInsp.inspectionNumber} marked as FAILED.`
          );
        }

        await loadInspections();
      } catch (err: any) {
        console.error('Error submitting inspection results:', err);
        toast.error(err?.response?.data?.message || err?.message || 'Failed to submit inspection results');
        return;
      }

      setActiveInspection(null);
      setActiveResults([]);

      if (overallStatus === 'FAILED') {
        const updated = { ...targetInsp, status: overallStatus as MockInspection['status'] };
        setNcrInspection(updated);
        setShowNCRPrompt(true);
      }
    },
    [activeInspection, loadInspections, toast]
  );

  const handleCreateNCR = useCallback((inspection: MockInspection) => {
    navigate('/manufacturing/qc/ncr', { state: { fromInspection: inspection.id } });
  }, [navigate]);

  const handleNCRSkip = useCallback(() => {
    // Inspection already marked FAILED, nothing more to do
  }, []);

  const existingResultsForActive = useMemo(() => {
    return activeResults;
  }, [activeResults]);

  const resultsForView = useMemo(() => {
    return activeResults;
  }, [activeResults]);

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Incoming Quality Inspection</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Inspect raw materials and purchased goods before acceptance
            </p>
          </div>
          <button
            onClick={() => {
              setPrefillGrnId(undefined);
              setShowNewInspection(true);
            }}
            className="px-3 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 whitespace-nowrap"
          >
            <i className="ri-add-line mr-1" />
            New Inspection
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <i className="ri-file-list-3-line text-gray-600 text-lg" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
                <p className="text-xs text-gray-500">Total Inspections</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <i className="ri-check-double-line text-emerald-600 text-lg" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-emerald-700">{stats.passed}</p>
                <p className="text-xs text-gray-500">Passed</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                <i className="ri-close-circle-line text-red-600 text-lg" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-red-700">{stats.failed}</p>
                <p className="text-xs text-gray-500">Failed</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <i className="ri-time-line text-amber-600 text-lg" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-amber-700">{stats.pending}</p>
                <p className="text-xs text-gray-500">Pending</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          <button
            onClick={() => setActiveTab('inspections')}
            className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap cursor-pointer ${activeTab === 'inspections'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            Inspections ({filtered.length})
          </button>
          <button
            onClick={() => setActiveTab('supplier_quality')}
            className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap cursor-pointer ${activeTab === 'supplier_quality'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            Supplier Quality
          </button>
        </div>

        {activeTab === 'inspections' && (
          <>
            {/* Filters */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex flex-col lg:flex-row gap-3 items-end">
                <div className="flex-1 w-full lg:w-auto">
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Search</label>
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search inspection, item, GRN..."
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
                <div className="w-full lg:w-44">
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Status</label>
                  <SearchableSelect
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={[
                      { value: '', label: 'All Status' },
                      { value: 'PASSED', label: 'Passed' },
                      { value: 'FAILED', label: 'Failed' },
                      { value: 'PENDING', label: 'Pending' },
                      { value: 'IN_PROGRESS', label: 'In Progress' },
                    ]}
                    placeholder="All Status"
                    allowClear
                  />
                </div>
                <div className="w-full lg:w-40">
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Source (GRN)</label>
                  <input
                    type="text"
                    value={sourceFilter}
                    onChange={(e) => setSourceFilter(e.target.value)}
                    placeholder="Filter by GRN..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div className="w-full lg:w-40">
                  <label className="text-xs font-medium text-gray-500 mb-1 block">From</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div className="w-full lg:w-40">
                  <label className="text-xs font-medium text-gray-500 mb-1 block">To</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <button
                  onClick={() => {
                    setStatusFilter('');
                    setSearch('');
                    setDateFrom('');
                    setDateTo('');
                    setSourceFilter('');
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-600 hover:bg-gray-50 whitespace-nowrap"
                >
                  <i className="ri-filter-off-line mr-1" />
                  Clear
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50/70 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-700">Incoming QC Records</span>
                  <span className="text-xs bg-gray-200/80 text-gray-700 font-medium px-2 py-0.5 rounded-full">
                    {filtered.length} {filtered.length === 1 ? 'item' : 'items'}
                  </span>
                  {groupedInspections.some((g) => g.items.length > 1) && (
                    <span className="text-xs bg-indigo-50 text-indigo-700 font-medium px-2 py-0.5 rounded-full border border-indigo-200">
                      Grouped by Sourced GRN
                    </span>
                  )}
                </div>
                {groupedInspections.some((g) => g.items.length > 1) && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={expandAll}
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-2 py-1 rounded transition-colors cursor-pointer"
                    >
                      <i className="ri-expand-up-down-line mr-1" />
                      Expand All
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={collapseAll}
                      className="text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 px-2 py-1 rounded transition-colors cursor-pointer"
                    >
                      <i className="ri-collapse-diagonal-line mr-1" />
                      Collapse All
                    </button>
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Insp No</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Source</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Item</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Batch</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-700">Sample</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-700">Result</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Inspector</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Date</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-700">Status</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-4 py-12 text-center text-gray-400">
                          <div className="flex flex-col items-center gap-2">
                            <i className="ri-inbox-line text-3xl" />
                            <p>No incoming inspections found</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      groupedInspections.map((grp) => {
                        const hasMultiple = grp.items.length > 1;
                        const isCollapsed = Boolean(collapsedGrns[grp.grnKey]);

                        if (hasMultiple) {
                          return (
                            <Fragment key={`group-${grp.grnKey}`}>
                              {/* Parent GRN Header Row */}
                              <tr
                                onClick={() => toggleGrn(grp.grnKey)}
                                className="bg-indigo-50/50 hover:bg-indigo-100/50 border-t-2 border-indigo-200/70 transition-colors cursor-pointer select-none"
                              >
                                <td className="px-4 py-3 font-semibold text-indigo-950">
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      className="w-6 h-6 flex items-center justify-center rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-transform cursor-pointer"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleGrn(grp.grnKey);
                                      }}
                                    >
                                      <i
                                        className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${
                                          isCollapsed ? '-rotate-90' : ''
                                        }`}
                                      />
                                    </button>
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 border border-indigo-300 font-bold">
                                        {grp.sourceNumber}
                                      </span>
                                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-600 text-white shadow-xs">
                                        {grp.items.length} Items
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-gray-600">
                                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                                    {grp.sourceType}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-gray-700">
                                  <div className="flex items-center gap-1.5 font-medium text-xs text-slate-800">
                                    <i className="ri-building-line text-slate-400" />
                                    <span>{grp.supplierName}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-400 italic">
                                  Multiple Batches
                                </td>
                                <td className="px-4 py-3 text-right text-gray-700 text-xs font-semibold">
                                  {grp.sampleQty} Total
                                </td>
                                <td className="px-4 py-3 text-center text-xs">
                                  <div className="flex items-center justify-center gap-1 font-medium">
                                    {grp.passedCount > 0 && <span className="text-emerald-600">{grp.passedCount} OK</span>}
                                    {grp.failedCount > 0 && <span className="text-red-600">{grp.failedCount} Fail</span>}
                                    {grp.pendingCount > 0 && <span className="text-amber-600">{grp.pendingCount} Pending</span>}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-gray-500 text-xs italic">
                                  Various
                                </td>
                                <td className="px-4 py-3 text-gray-600 text-xs font-medium">
                                  {formatDateDisplay(grp.scheduledDate)}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusBadge(grp.overallStatus)}`}>
                                    {grp.overallStatus}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleGrn(grp.grnKey);
                                    }}
                                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-1 cursor-pointer"
                                  >
                                    <span>{isCollapsed ? 'View Items' : 'Hide Items'}</span>
                                    <i className={`ri-arrow-down-s-line text-xs transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                                  </button>
                                </td>
                              </tr>

                              {/* Child items when expanded */}
                              {!isCollapsed &&
                                grp.items.map((insp) => (
                                  <tr
                                    key={insp.id}
                                    className="hover:bg-slate-50/80 bg-slate-50/25 border-l-4 border-indigo-400 transition-colors"
                                  >
                                    <td className="px-4 py-3 font-medium text-gray-900">
                                      <div className="flex items-center gap-2 pl-4">
                                        <i className="ri-corner-down-right-line text-indigo-400 text-xs shrink-0" />
                                        <span className="font-mono text-xs font-semibold text-indigo-950">
                                          {insp.inspectionNumber}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-gray-600">
                                      <span className="text-xs font-mono text-gray-500">{insp.sourceNumber}</span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-700">
                                      <div className="flex flex-col">
                                        <span className="font-medium text-sm text-gray-900">{insp.itemName}</span>
                                        {insp.variantName && (
                                          <span className="text-xs text-gray-400">{insp.variantName}</span>
                                        )}
                                        {insp.itemCode && (
                                          <span className="text-[10px] text-gray-400 font-mono">{insp.itemCode}</span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-gray-600">
                                      {insp.batchNumber || insp.lotNumber ? (
                                        <div className="flex flex-col gap-0.5">
                                          {insp.batchNumber && (
                                            <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-mono font-bold w-fit">
                                              <i className="ri-barcode-line mr-1 text-blue-500" />
                                              {insp.batchNumber}
                                            </span>
                                          )}
                                          {insp.lotNumber && <span className="text-xs text-gray-400">LOT: {insp.lotNumber}</span>}
                                        </div>
                                      ) : (
                                        <span className="text-xs text-gray-400">—</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-right text-gray-700 text-sm">
                                      {insp.sampleQty} {insp.unit}
                                      <div className="text-xs text-gray-400">{insp.samplingPlan.replace('_', ' ')}</div>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      {insp.status === 'PASSED' && (
                                        <span className="text-emerald-600 font-medium text-sm">{insp.passedQty} OK</span>
                                      )}
                                      {insp.status === 'FAILED' && (
                                        <span className="text-red-600 font-medium text-sm">{insp.failedQty} Fail</span>
                                      )}
                                      {(insp.status === 'PENDING' || insp.status === 'IN_PROGRESS') && (
                                        <span className="text-gray-400 text-sm">—</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-gray-700 text-sm">
                                      {insp.inspectorName || <span className="text-gray-400 text-xs">Unassigned</span>}
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 text-sm">
                                      <div className="flex flex-col">
                                        <span className="font-medium text-xs text-slate-800">{formatDateDisplay(insp.scheduledDate)}</span>
                                        {insp.completedDate && (
                                          <span className="text-[10px] text-gray-400">Done: {formatDateDisplay(insp.completedDate)}</span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(
                                          insp.status
                                        )}`}
                                      >
                                        {insp.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <div className="flex items-center justify-center gap-1">
                                        {insp.status === 'PENDING' && (
                                          <button
                                            onClick={() => handleStartInspection(insp)}
                                            className="px-2 py-1 text-xs bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded font-medium cursor-pointer"
                                            title="Start Inspection"
                                          >
                                            <i className="ri-play-circle-line mr-0.5" />
                                            Start
                                          </button>
                                        )}
                                        {insp.status !== 'PENDING' && (
                                          <button
                                            onClick={() => handleViewInspection(insp)}
                                            className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded cursor-pointer"
                                            title="View"
                                          >
                                            <i className="ri-eye-line" />
                                          </button>
                                        )}
                                        {insp.status === 'FAILED' && (
                                          <button
                                            onClick={() => navigate('/manufacturing/qc/ncr')}
                                            className="px-2 py-1 text-xs bg-red-50 text-red-700 hover:bg-red-100 rounded font-medium cursor-pointer"
                                            title="NCR"
                                          >
                                            NCR
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                            </Fragment>
                          );
                        }

                        // Single item group
                        const insp = grp.items[0];
                        return (
                          <tr key={insp.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-900 font-mono text-xs">
                              {insp.inspectionNumber}
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              <div className="flex flex-col">
                                <span className="font-medium text-sm">{insp.sourceNumber}</span>
                                <span className="text-xs text-gray-400">{insp.sourceType}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              <div className="flex flex-col">
                                <span className="font-medium text-sm">{insp.itemName}</span>
                                {insp.variantName && (
                                  <span className="text-xs text-gray-400">{insp.variantName}</span>
                                )}
                                {insp.itemCode && (
                                  <span className="text-[10px] text-gray-400 font-mono">{insp.itemCode}</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              {insp.batchNumber || insp.lotNumber ? (
                                <div className="flex flex-col gap-0.5">
                                  {insp.batchNumber && (
                                    <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-mono font-bold w-fit">
                                      <i className="ri-barcode-line mr-1 text-blue-500" />
                                      {insp.batchNumber}
                                    </span>
                                  )}
                                  {insp.lotNumber && <span className="text-xs text-gray-400">LOT: {insp.lotNumber}</span>}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-700 text-sm">
                              {insp.sampleQty} {insp.unit}
                              <div className="text-xs text-gray-400">{insp.samplingPlan.replace('_', ' ')}</div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {insp.status === 'PASSED' && (
                                <span className="text-emerald-600 font-medium text-sm">{insp.passedQty} OK</span>
                              )}
                              {insp.status === 'FAILED' && (
                                <span className="text-red-600 font-medium text-sm">{insp.failedQty} Fail</span>
                              )}
                              {(insp.status === 'PENDING' || insp.status === 'IN_PROGRESS') && (
                                <span className="text-gray-400 text-sm">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-gray-700 text-sm">
                              {insp.inspectorName || <span className="text-gray-400 text-xs">Unassigned</span>}
                            </td>
                            <td className="px-4 py-3 text-gray-600 text-sm">
                              <div className="flex flex-col">
                                <span className="font-medium text-xs text-slate-800">{formatDateDisplay(insp.scheduledDate)}</span>
                                {insp.completedDate && (
                                  <span className="text-[10px] text-gray-400">Done: {formatDateDisplay(insp.completedDate)}</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(
                                  insp.status
                                )}`}
                              >
                                {insp.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {insp.status === 'PENDING' && (
                                  <button
                                    onClick={() => handleStartInspection(insp)}
                                    className="px-2 py-1 text-xs bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded font-medium cursor-pointer"
                                    title="Start Inspection"
                                  >
                                    <i className="ri-play-circle-line mr-0.5" />
                                    Start
                                  </button>
                                )}
                                {insp.status !== 'PENDING' && (
                                  <button
                                    onClick={() => handleViewInspection(insp)}
                                    className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded cursor-pointer"
                                    title="View"
                                  >
                                    <i className="ri-eye-line" />
                                  </button>
                                )}
                                {insp.status === 'FAILED' && (
                                  <button
                                    onClick={() => navigate('/manufacturing/qc/ncr')}
                                    className="px-2 py-1 text-xs bg-red-50 text-red-700 hover:bg-red-100 rounded font-medium cursor-pointer"
                                    title="NCR"
                                  >
                                    NCR
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {activeTab === 'supplier_quality' && <SupplierQualityTab inspections={inspections} />}
      </div>

      {/* Modals */}
      <NewInspectionSlideOver
        isOpen={showNewInspection}
        onClose={() => {
          setShowNewInspection(false);
          setPrefillGrnId(undefined);
          setPrefillData(null);
        }}
        onSave={handleNewInspection}
        prefillGrnId={prefillGrnId}
        prefillData={prefillData || undefined}
      />

      {activeInspection && !showViewModal && (
        <InspectionFormModal
          inspection={activeInspection}
          existingResults={existingResultsForActive}
          onClose={() => setActiveInspection(null)}
          onSubmit={handleSubmitInspection}
        />
      )}

      {activeInspection && showViewModal && (
        <ViewInspectionModal
          inspection={activeInspection}
          results={resultsForView}
          onClose={() => {
            setShowViewModal(false);
            setActiveInspection(null);
          }}
        />
      )}

      <NCRPromptModal
        inspection={ncrInspection}
        onClose={() => {
          setShowNCRPrompt(false);
          setNcrInspection(null);
        }}
        onCreateNCR={handleCreateNCR}
        onSkip={handleNCRSkip}
      />
    </AppLayout>
  );
}